import React, { memo, useCallback, useContext, useMemo, useState } from 'react'
import { MenuActionItem } from 'src/interfaces/ui'
import { ActionMenu } from 'src/components/shared/ActionMenu'
import { CancelMinor, ViewMinor } from '@shopify/polaris-icons'
import tw from 'twin.macro'
import { SensitiveActionModal } from 'src/components/shared/SensitiveActionModal'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { useDispatch } from 'react-redux'
import { DeleteMCPayment, FetchMCPaymentInvoice } from 'src/constants/actionTypes'
import { showToast } from 'src/utils/toast'
import { PaymentStatus } from 'src/constants/payment/enums'
import { MCPaymentInvoiceModal } from '../../MCPaymentInvoiceModal'
import { UserPermission } from 'src/constants/user'
import { handleApiError } from 'src/utils/errorHandlers'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { MCPaymentsTableContext } from '../../payments-table/MCPaymentsTable.context'
import { ReloadWrapperContext } from 'src/components/wrapper/reload-wrapper/ReloadWrapper.context'

const MCPaymentsItemActionMenuComponent = () => {
  const dispatch = useDispatch()

  const { reload } = useContext(ReloadWrapperContext)
  const { paymentToAction, actionAnchor, closeMCPaymentsItemAction, clearMCPaymentsItemAction } =
    useContext(MCPaymentsTableContext)

  const { hasPermission } = useCheckPermissions()

  const isOpen = Boolean(actionAnchor)

  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [invoice, setInvoice] = useState<any>(null)

  const clearInvoice = useCallback(() => {
    setInvoice(null)
  }, [])

  const handleOpenConfirm = useCallback(() => {
    setIsConfirmOpen(true)
  }, [])

  const handleCloseConfirm = useCallback(() => {
    setIsConfirmOpen(false)
  }, [])

  const handleDeleteMCPayment = useCallback(async () => {
    if (!paymentToAction) {
      return
    }

    try {
      await createAsyncAction(
        dispatch,
        DeleteMCPayment.request({
          paymentIds: [paymentToAction.id],
        }),
      )

      reload()

      showToast('MCPayment is deleted.')
    } catch (err) {
      handleApiError(err)
    }

    handleCloseConfirm()
  }, [paymentToAction, reload])

  const handleDownloadInvoice = useCallback(async () => {}, [invoice])

  const actionItems: MenuActionItem[] = useMemo(() => {
    const res: any = []

    if (paymentToAction?.status === PaymentStatus.Paid) {
      res.push({
        label: 'View invoice',
        icon: <ViewMinor width={20} height={20} />,
        onClick: async () => {
          if (!paymentToAction) {
            return
          }

          const buffer: any = await createAsyncAction(
            dispatch,
            FetchMCPaymentInvoice.request({
              paymentId: paymentToAction.id,
            }),
          )

          const blob = new Blob([buffer], { type: 'application/pdf' })

          setInvoice(URL.createObjectURL(blob))

          closeMCPaymentsItemAction()
        },
      })
    }

    if (
      paymentToAction?.status &&
      [PaymentStatus.NotPaid, PaymentStatus.RequiresFunding].includes(paymentToAction.status) &&
      hasPermission(UserPermission.PaymentsDelete)
    ) {
      res.push({
        label: 'Delete MCPayment',
        icon: <CancelMinor width={20} height={20} />,
        onClick: () => {
          if (!paymentToAction) {
            return
          }

          handleOpenConfirm()
          closeMCPaymentsItemAction()
        },
      })
    }

    return res
  }, [paymentToAction, hasPermission])

  return (
    <>
      <ActionMenu
        anchorEl={actionAnchor}
        itemClassName={tw`min-w-[208px]`}
        isOpen={isOpen}
        items={actionItems}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={clearMCPaymentsItemAction}
      />
      <SensitiveActionModal
        isOpen={isConfirmOpen}
        title="Delete payment"
        text="Are you sure you want to delete payment?"
        submitLabel="Delete"
        onClose={handleCloseConfirm}
        onSubmit={handleDeleteMCPayment}
      />
      <MCPaymentInvoiceModal
        isOpen={Boolean(invoice)}
        invoice={invoice}
        onClose={clearInvoice}
        onDownload={handleDownloadInvoice}
      />
    </>
  )
}

export const MCPaymentsItemActionMenu = memo(MCPaymentsItemActionMenuComponent)
